import { FreeToolIcon } from '@/components/free-tools/tool-icon';
import type {
  FreeToolBrand,
  FreeToolCategory,
  FreeToolDefinition,
} from '@/lib/free-tools/registry';
import { cn } from '@/lib/utils';

/** The platform chip row under every tool. Order is the row's order. */
export const PLATFORM_TOOLS = [
  { name: 'YouTube', href: '/tools/youtube-video-downloader' },
  { name: 'Instagram', href: '/tools/instagram-downloader' },
  { name: 'TikTok', href: '/tools/tiktok-downloader' },
  { name: 'X', href: '/tools/twitter-video-to-mp3' },
  { name: 'Facebook', href: '/tools/facebook-video-downloader' },
  { name: 'Reddit', href: '/tools/reddit-video-downloader' },
  { name: 'Vimeo', href: '/tools/vimeo-to-mp3' },
  { name: 'SoundCloud', href: '/tools/soundcloud-downloader' },
] as const satisfies ReadonlyArray<{ name: FreeToolBrand; href: string }>;

/** Tile face (`color`) and underside (`base`) per brand. Marks live in `public/images/tools/platforms/`. */
const BRAND_TILES: Record<FreeToolBrand, { color: string; base: string; file: string }> = {
  YouTube: { color: '#f52242', base: '#a90022', file: 'youtube' },
  Instagram: { color: '#ff9c45', base: '#9136d5', file: 'instagram' },
  TikTok: { color: '#34343b', base: '#111116', file: 'tiktok' },
  X: { color: '#444449', base: '#131317', file: 'x' },
  Facebook: { color: '#388df8', base: '#0853b2', file: 'facebook' },
  Reddit: { color: '#ff702b', base: '#c13e05', file: 'reddit' },
  Vimeo: { color: '#40c4e8', base: '#0c799e', file: 'vimeo' },
  SoundCloud: { color: '#ff952c', base: '#c44a00', file: 'soundcloud' },
  Loom: { color: '#7d78ff', base: '#3d36c9', file: 'loom' },
  TED: { color: '#f2412f', base: '#a3150a', file: 'ted' },
  'Apple Podcasts': { color: '#c25cf0', base: '#7322a8', file: 'applepodcasts' },
};

/** Tools with no platform share one tile colour per category, so the grid reads as groups. */
const CATEGORY_TILES: Record<FreeToolCategory, { color: string; base: string }> = {
  download: { color: '#5b8cff', base: '#2547b8' },
  'transcribe-convert': { color: '#2fcf9f', base: '#0b7a5c' },
  repurpose: { color: '#f45aa0', base: '#a3135a' },
};

const BRAND_BY_NAME: ReadonlyMap<string, FreeToolBrand> = new Map(
  (Object.keys(BRAND_TILES) as FreeToolBrand[]).map((b) => [b, b])
);

function tileStyle(color: string, base: string, background?: string): React.CSSProperties {
  return {
    background: background ?? `linear-gradient(165deg,${color},${base})`,
    boxShadow: `inset 0 1px 1px #ffffff80, inset 0 -2px 1px ${base}, 0 2px 0 ${base}, 0 5px 10px #00000030`,
  };
}

/** Official Simple Icons marks on a glossy, raised tile inspired by LinkToKeep. */
export function PlatformIcon({ platform, className }: { platform: string; className?: string }) {
  const brand = BRAND_BY_NAME.get(platform);
  if (!brand) return null;
  const item = BRAND_TILES[brand];
  return (
    <span
      aria-hidden
      className={cn('platform-tile inline-flex shrink-0 items-center justify-center', className)}
      style={tileStyle(
        item.color,
        item.base,
        brand === 'Instagram' ? 'linear-gradient(165deg,#ffd26a,#ff6057 48%,#ce39ba 80%)' : undefined
      )}
    >
      {/* Local, attributed brand asset. Plain img keeps tiny SVGs out of the image optimizer. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={`/images/tools/platforms/${item.file}.svg`}
        alt=""
        className="h-[58%] w-[58%] object-contain"
        width={24}
        height={24}
      />
    </span>
  );
}

/**
 * The one tile every tool card renders: the brand mark when the tool works on a platform,
 * otherwise its glyph on the same glossy tile in its category colour.
 */
export function ToolTileIcon({
  tool,
  className = 'h-10 w-10',
}: {
  tool: Pick<FreeToolDefinition, 'brand' | 'category' | 'iconName'>;
  className?: string;
}) {
  if (tool.brand) return <PlatformIcon platform={tool.brand} className={className} />;
  const tile = CATEGORY_TILES[tool.category];
  return (
    <span
      aria-hidden
      className={cn('platform-tile inline-flex shrink-0 items-center justify-center', className)}
      style={tileStyle(tile.color, tile.base)}
    >
      <FreeToolIcon name={tool.iconName} className="relative h-[50%] w-[50%] text-white" />
    </span>
  );
}
